import { useState, useRef } from 'react'
import { motion, useInView } from 'framer-motion'
import { HiPlus } from 'react-icons/hi'

interface FAQItem {
  question: string
  answer: string
}

const FAQ_ITEMS: FAQItem[] = [
  {
    question: 'How is StatScout different from tipster services?',
    answer: "We don't give tips or claim to predict winners. Instead, we provide transparent, data-driven analytics that show you exactly why a bet might be worth considering. Every suggestion includes the underlying statistics, sample sizes, and methodology - so you can make informed decisions rather than following blind tips.",
  },
  {
    question: 'What leagues and markets do you cover?',
    answer: "We cover 6 major leagues: Premier League, Championship, La Liga, Bundesliga, Serie A, and Ligue 1. Within each league, we analyze 60+ betting markets including match results, goals, corners, cards, player props, and many more.",
  },
  {
    question: 'How often are stats updated?',
    answer: "Fixture data updates every 3-6 hours for upcoming matches. During live matches, stats refresh every 1-2 minutes including possession, shots, corners, cards, and other key metrics. Completed fixture stats are sealed and preserved for historical analysis.",
  },
  {
    question: 'What is the Dixon-Coles model?',
    answer: "Dixon-Coles is an industry-standard statistical model for football match prediction, developed by academic researchers. It accounts for home advantage, team attack/defense strengths, and the correlation between low-scoring results. We use this model alongside Poisson and negative binomial distributions to calculate probabilities across all markets.",
  },
  {
    question: 'Can I cancel my subscription anytime?',
    answer: "Yes, absolutely. You can cancel your subscription at any time with no questions asked. If you cancel during your free trial, you won't be charged at all. If you cancel after, your access continues until the end of your billing period.",
  },
  {
    question: 'Do you offer refunds?',
    answer: "We offer a 7-day free trial so you can evaluate the platform before committing. If you're unsatisfied within your first paid month, contact us and we'll work with you to make it right.",
  },
]

export function FAQSection() {
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index)
  }

  return (
    <section ref={ref} className="faq-section">
      <motion.h2
        className="faq-title"
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5 }}
      >
        Frequently Asked Questions
      </motion.h2>

      <div className="faq-list">
        {FAQ_ITEMS.map((item, index) => (
          <motion.div
            key={index}
            className={`faq-item ${openIndex === index ? 'open' : ''}`}
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.4, delay: index * 0.1 }}
          >
            <button className="faq-question" onClick={() => toggleFAQ(index)}>
              <span>{item.question}</span>
              <HiPlus className="faq-icon" />
            </button>
            <div className="faq-answer">
              <p className="faq-answer-text">{item.answer}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  )
}
